import { Entity } from "@common/types/entity";
import classNames from "classnames";
import { useMemo } from "react";
import { fullDateLongFormat } from "@common/helpers/date";
import { format, parseISO } from "date-fns";
import { Field } from "@design-system/Field/Field";
import { Input } from "@design-system/Input/Input";
import { EntitySelect } from "@components/entity/EntitySelect";
import { SpreadsheetGrid } from "@components/spreadsheet/SpreadsheetGrid";
import { Account } from "@common/types/domains/accounting/account";
import { CellType } from "@components/spreadsheet/cells";
import { ColumnDef } from "@components/spreadsheet/types";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { currencyFormatter } from "@common/helpers/currency";
import { useEntities } from "@hooks/useEntities";
import styles from "./FinancialAccountOpeningBalance.module.scss";

export interface JournalEntryPreviewSheetRow {
    accountId?: string;
    debitAmount?: number | null;
    creditAmount?: number | null;
    description?: string;
}

interface Props {
    lines: JournalEntryPreviewSheetRow[];
    title: string;
    date: string;
    entity: Entity;
    accounts: Account[];
    className?: string;
    spreadsheetClassName?: string;
}

export const JournalEntryPreview: React.FC<Props> = ({
    lines,
    title,
    date,
    entity,
    accounts,
    className,
    spreadsheetClassName,
}) => {
    const dateString = useMemo(
        () => format(parseISO(date), fullDateLongFormat),
        [date],
    );
    const entities = useEntities();

    const columns: ColumnDef[] = useMemo(
        () => [
            {
                field: "accountId",
                label: "Account",
                cellType: CellType.ACCOUNT_SEARCH,
                accounts,
                aggregation: () => "Total",
            },
            {
                field: "debitAmount",
                label: "Debit",
                cellType: CellType.NUMBER,
                isMonetary: true,
                aggregation: (data) => {
                    const sum = sumMonetaryValues(
                        data.map(({ debitAmount }) => debitAmount ?? 0),
                    );

                    return currencyFormatter.format(sum);
                },
            },
            {
                field: "creditAmount",
                label: "Credit",
                cellType: CellType.NUMBER,
                isMonetary: true,
                aggregation: (data) => {
                    const sum = sumMonetaryValues(
                        data.map(({ creditAmount }) => creditAmount ?? 0),
                    );

                    return currencyFormatter.format(sum);
                },
            },
            {
                field: "description",
                label: "Description",
                cellType: CellType.STRING,
            },
        ],
        [accounts],
    );

    return (
        <div className={classNames(styles.journalEntryPreview, className)}>
            <header className={styles.header}>
                <h5>{title}</h5>

                <Field.Root className={styles.date}>
                    <Input value={dateString} disabled size="lg" />
                </Field.Root>
                <EntitySelect
                    entities={entities}
                    selectedEntity={entity}
                    onChange={() => {}}
                    disabled
                />
            </header>
            <SpreadsheetGrid
                data={lines}
                columns={columns}
                isCellEditable={() => false}
                showTotalsRow
                className={classNames(styles.spreadsheet, spreadsheetClassName)}
                gridTemplateColumns="1.5fr 160px 160px 1fr"
            />
        </div>
    );
};
